import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import DeleteIcon from '@mui/icons-material/Delete'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useCallback, useState } from 'react'
import ActionQrDialog from '../components/ActionQrDialog'
import {
  AddSiteAdminDocument,
  AdminUsersDocument,
  Gender,
  GetCurrentUserDocument,
  RemoveSiteAdminDocument,
  SiteRole,
} from '../gql/graphql'
import { useSiteContext } from '../hooks'

gql`
  query AdminUsers($siteId: UUID!) {
    adminUsers(siteId: $siteId) {
      id
      openid
      name
      avatar
      mobile
      gender
      role
    }
  }
`

gql`
  mutation AddSiteAdmin($siteId: ID!, $userId: ID!, $type: String!) {
    addSiteAdmin(siteId: $siteId, userId: $userId, type: $type) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

gql`
  mutation RemoveSiteAdmin($siteId: ID!, $userId: ID!) {
    removeSiteAdmin(siteId: $siteId, userId: $userId) {
      ... on GraphqlSuccess {
        success
      }
    }
  }
`

export default function UsersPage() {
  const { currentSite } = useSiteContext()
  const { data, loading, error, refetch } = useQuery(AdminUsersDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })
  const { data: currentUserData } = useQuery(GetCurrentUserDocument)
  const [removeSiteAdmin] = useMutation(RemoveSiteAdminDocument)
  const [addSiteAdmin] = useMutation(AddSiteAdminDocument)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<{
    id: string
    name: string | null | undefined
  } | null>(null)
  const [roleSelectionOpen, setRoleSelectionOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<'admin' | 'super_admin'>('admin')
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false)

  const handleDeleteClick = (userId: string, userName: string | null | undefined) => {
    setUserToDelete({ id: userId, name: userName })
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!userToDelete || !currentSite) return

    try {
      await removeSiteAdmin({
        variables: {
          siteId: currentSite.id,
          userId: userToDelete.id,
        },
      })
      await refetch()
    } catch (err) {
      message.error(`删除失败: ${err}`)
    } finally {
      setDeleteDialogOpen(false)
      setUserToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  const handleQrScanned = useCallback(
    async (user: { id?: string | null; openid: string }) => {
      if (!currentSite || !user.id) {
        message.error('未找到用户')
        return
      }

      try {
        // Add the user as admin or super_admin
        const result = await addSiteAdmin({
          variables: {
            siteId: currentSite.id,
            userId: user.id,
            type: selectedRole,
          },
        })

        // Check if the mutation returned an error
        if (result.data?.addSiteAdmin.__typename === 'GraphqlRequestError') {
          message.error(`添加失败: ${result.data.addSiteAdmin.message}`)
          return
        }

        // Refresh the user list and close the dialog
        await refetch()
        setAddUserDialogOpen(false)
      } catch (err) {
        message.error(`添加失败: ${err}`)
      }
    },
    [currentSite, addSiteAdmin, selectedRole, refetch],
  )

  const handleAddUserClick = () => {
    setSelectedRole('admin')
    setRoleSelectionOpen(true)
  }

  const handleRoleConfirm = () => {
    setRoleSelectionOpen(false)
    setAddUserDialogOpen(true)
  }

  const handleRoleCancel = () => {
    setRoleSelectionOpen(false)
  }

  if (loading) return <CircularProgress />
  if (error) return <Alert severity="error">{error.message}</Alert>

  const users = data?.adminUsers ?? []
  // Type assertion needed because fragment refs don't expose fields directly
  const currentUserId =
    (currentUserData?.currentUser as { id?: string | null })?.id ?? null

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">用户管理</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleAddUserClick}
        >
          添加用户
        </Button>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>姓名</TableCell>
              <TableCell>手机号</TableCell>
              <TableCell>性别</TableCell>
              <TableCell>角色</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.mobile}</TableCell>
                <TableCell>
                  {user.gender === Gender.Male
                    ? '男'
                    : user.gender === Gender.Female
                      ? '女'
                      : '-'}
                </TableCell>
                <TableCell>
                  {user.role === SiteRole.SuperAdmin
                    ? '超级管理员'
                    : user.role === SiteRole.Admin
                      ? '管理员'
                      : '成员'}
                </TableCell>
                <TableCell>
                  {user.id !== currentUserId && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(user.id as string, user.name)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} disableRestoreFocus>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除用户 "{userToDelete?.name}" 吗？此操作无法撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>取消</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={roleSelectionOpen} onClose={handleRoleCancel} disableRestoreFocus>
        <DialogTitle>选择用户角色</DialogTitle>
        <DialogContent>
          <FormControl>
            <RadioGroup
              value={selectedRole}
              onChange={(e) =>
                setSelectedRole(e.target.value as 'admin' | 'super_admin')
              }
            >
              <FormControlLabel value="admin" control={<Radio />} label="管理员" />
              <FormControlLabel
                value="super_admin"
                control={<Radio />}
                label="超级管理员"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRoleCancel}>取消</Button>
          <Button onClick={handleRoleConfirm} variant="contained">
            确定
          </Button>
        </DialogActions>
      </Dialog>

      {currentSite && (
        <ActionQrDialog
          open={addUserDialogOpen}
          onClose={() => setAddUserDialogOpen(false)}
          action="addadmin"
          actionId={currentSite.id}
          title="扫码添加管理员"
          onScanned={handleQrScanned}
          extraInfo={selectedRole === 'super_admin' ? 's' : ''}
        />
      )}
    </Box>
  )
}
